import React, { useState, useEffect } from 'react';
import { getSpendingTrends, getExpenses, getCategoryBreakdown } from '../services/api';
import { getUserProfile } from '../services/settingsApi';
import { formatCurrency } from '../utils/numberFormat';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatPeriod(dateStr) {
  const d = new Date(dateStr + '-01');
  return `${MONTHS[d.getMonth()]}-${String(d.getFullYear()).slice(2)}`;
}

function AnalyticsDashboard() {
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [topExpenses, setTopExpenses] = useState([]);
  const [topLoading, setTopLoading] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);

  const [userCurrency, setUserCurrency] = useState('SGD');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    getUserProfile().then(res => {
      if (res.profile?.default_currency_code) {
        setUserCurrency(res.profile.default_currency_code);
      }
    }).catch(() => {});
  }, []);

  // Fetch monthly breakdown
  useEffect(() => {
    const fetchMonthly = async () => {
      try {
        setLoading(true);
        const result = await getSpendingTrends(null, null, 'monthly');
        const trends = (result.data || [])
          .map(t => ({ ...t, date: t.date || t.month }))
          .filter(t => t.totalSpend > 0 && t.date)
          .sort((a, b) => b.date.localeCompare(a.date));
        setMonthlyData(trends);
        if (trends.length > 0 && !selectedPeriod) {
          setSelectedPeriod(trends[0].date);
        }
      } catch (err) {
        console.error('Failed to load monthly data:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };
    fetchMonthly();
  }, []);

  // Fetch top 20 expenses for selected period
  useEffect(() => {
    if (!selectedPeriod) return;
    const fetchTop = async () => {
      try {
        setTopLoading(true);
        const [year, month] = selectedPeriod.split('-').map(Number);
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
        const result = await getExpenses({
          startDate,
          endDate,
          sortBy: 'amount_base',
          sortOrder: 'desc',
          limit: 20,
        });
        // Client-side re-sort by base currency to handle any NULL amount_in_default_currency
        const expenses = (result.expenses || []).sort((a, b) => {
          const aAmt = a.amount_in_default_currency ?? a.amount ?? 0;
          const bAmt = b.amount_in_default_currency ?? b.amount ?? 0;
          return bAmt - aAmt;
        });
        setTopExpenses(expenses);
      } catch (err) {
        console.error('Failed to load top expenses:', err);
        setTopExpenses([]);
      } finally {
        setTopLoading(false);
      }
    };
    fetchTop();
  }, [selectedPeriod]);

  // Fetch category breakdown for selected period
  useEffect(() => {
    if (!selectedPeriod) return;
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const [year, month] = selectedPeriod.split('-').map(Number);
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
        const result = await getCategoryBreakdown(startDate, endDate);
        const breakdown = (result.data || []).sort((a, b) => b.totalAmount - a.totalAmount);
        setCategoryData(breakdown);
      } catch (err) {
        console.error('Failed to load category breakdown:', err);
        setCategoryData([]);
      } finally {
        setCategoryLoading(false);
      }
    };
    fetchCategories();
  }, [selectedPeriod]);

  // Calculate average (only months with total > $1,000)
  const qualifyingMonths = monthlyData.filter(m => m.totalSpend > 1000);
  const avgMonthly = qualifyingMonths.length > 0
    ? qualifyingMonths.reduce((sum, m) => sum + m.totalSpend, 0) / qualifyingMonths.length
    : 0;

  const containerStyle = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: isMobile ? '8px' : '24px',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: isMobile ? '12px' : '14px',
  };

  const thStyle = {
    padding: isMobile ? '8px 6px' : '10px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #E8E8E8',
    color: '#71717A',
    fontWeight: '600',
    fontSize: isMobile ? '11px' : '13px',
  };

  const tdStyle = {
    padding: isMobile ? '8px 6px' : '10px 12px',
    borderBottom: '1px solid #F5F5F5',
    color: '#1f2937',
  };

  const sectionStyle = {
    backgroundColor: 'white',
    borderRadius: '14px',
    padding: isMobile ? '12px' : '20px',
    marginBottom: '20px',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)',
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: '#71717A' }}>
        <div style={{
          display: 'inline-block', width: '40px', height: '40px',
          border: '4px solid #f3f3f3', borderTop: '4px solid #3b82f6',
          borderRadius: '50%', animation: 'spin 1s linear infinite',
          marginBottom: '16px',
        }} />
        <p>Loading analytics...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#991b1b', backgroundColor: '#fee2e2', borderRadius: '10px', margin: '20px' }}>
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Table 1: Monthly Breakdown */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: isMobile ? '16px' : '18px', fontWeight: '600', letterSpacing: '-0.025em', color: '#1f2937' }}>
          Monthly Breakdown
        </h3>
        {monthlyData.length === 0 ? (
          <p style={{ color: '#71717A', textAlign: 'center', padding: '20px' }}>No expense data found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Period</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total Expenses</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>% Gap</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((month) => {
                  const gap = avgMonthly > 0 ? ((month.totalSpend - avgMonthly) / avgMonthly) * 100 : 0;
                  const isAbove = gap > 0;
                  const gapColor = gap === 0 ? '#71717A' : isAbove ? '#dc2626' : '#16a34a';
                  return (
                    <tr key={month.date}>
                      <td style={tdStyle}>{formatPeriod(month.date)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                        {formatCurrency(month.totalSpend, userCurrency)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: gapColor, fontWeight: '600' }}>
                        {avgMonthly > 0 ? `${isAbove ? '+' : ''}${gap.toFixed(1)}%` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {avgMonthly > 0 && (
              <p style={{ fontSize: '11px', color: '#A1A1AA', marginTop: '8px', textAlign: 'right' }}>
                Avg: {formatCurrency(avgMonthly, userCurrency)}/mo (months &gt; {formatCurrency(1000, userCurrency)} only)
              </p>
            )}
          </div>
        )}
      </div>

      {/* Table 2: Top 20 Expenses by Month */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '18px', fontWeight: '600', letterSpacing: '-0.025em', color: '#1f2937' }}>
            Top 20 Expenses
          </h3>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={{
              padding: '6px 12px',
              borderRadius: '10px',
              border: '1px solid #E0E0E0',
              fontSize: '13px',
              backgroundColor: 'white',
              cursor: 'pointer',
            }}
          >
            {monthlyData.map((m) => (
              <option key={m.date} value={m.date}>{formatPeriod(m.date)}</option>
            ))}
          </select>
        </div>

        {topLoading ? (
          <p style={{ color: '#71717A', textAlign: 'center', padding: '20px' }}>Loading...</p>
        ) : topExpenses.length === 0 ? (
          <p style={{ color: '#71717A', textAlign: 'center', padding: '20px' }}>No expenses found for this period.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Expense Name</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                  <th style={thStyle}>Category</th>
                </tr>
              </thead>
              <tbody>
                {topExpenses.map((exp, i) => (
                  <tr key={exp.id || i}>
                    <td style={{ ...tdStyle, maxWidth: isMobile ? '140px' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {exp.vendor_name || 'Unknown'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600', whiteSpace: 'nowrap' }}>
                      {formatCurrency(exp.amount_in_default_currency ?? exp.amount, userCurrency)}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {exp.category ? exp.category.charAt(0).toUpperCase() + exp.category.slice(1) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Table 3: Category Breakdown by Month */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: isMobile ? '16px' : '18px', fontWeight: '600', letterSpacing: '-0.025em', color: '#1f2937' }}>
          Category Breakdown {selectedPeriod && `- ${formatPeriod(selectedPeriod)}`}
        </h3>

        {categoryLoading ? (
          <p style={{ color: '#71717A', textAlign: 'center', padding: '20px' }}>Loading...</p>
        ) : categoryData.length === 0 ? (
          <p style={{ color: '#71717A', textAlign: 'center', padding: '20px' }}>No category data for this period.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Category</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total Amount</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {categoryData.map((cat) => (
                  <tr key={cat.category}>
                    <td style={tdStyle}>
                      {cat.category ? cat.category.charAt(0).toUpperCase() + cat.category.slice(1) : '-'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(cat.totalAmount, userCurrency)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {cat.percentage.toFixed(1)}%
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #E8E8E8' }}>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>Total</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                    {formatCurrency(categoryData.reduce((sum, c) => sum + c.totalAmount, 0), userCurrency)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnalyticsDashboard;
